import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Users, Gift, CheckCircle2, XCircle, Search, Trash2, Plus, RefreshCw, Trophy, Tag, Send, ShieldCheck, Award, UserCheck, UserX, Inbox, Check, X, Zap, PhoneCall, MessageCircle, LogIn, Eye
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { JHARKHAND_DISTRICTS } from "@/data/jharkhand";
import JHARKHAND_BLOCKS from "@/jharkhand_blocks.json";

const normalizeDistrictName = (d) => {
  const map = {
    "Jamshedpur": "East Singhbhum",
    "Chaibasa": "West Singhbhum",
    "Saraikela": "Seraikela",
    "Koderma": "Kodarma"
  };
  return map[d] || d;
};

export default function AdminReferrals() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("applications"); // "applications" | "recharges" | "mitras" | "joins" | "coupons" | "leaderboard"
  const [overview, setOverview] = useState(null);
  const [mitras, setMitras] = useState([]);
  const [applications, setApplications] = useState([]);
  const [recharges, setRecharges] = useState([]);
  const [joins, setJoins] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Appoint Mitra Dialog State
  const [appointDialogOpen, setAppointDialogOpen] = useState(false);
  const [mitraName, setMitraName] = useState("");
  const [mitraPhone, setMitraPhone] = useState("");
  const [mitraEmail, setMitraEmail] = useState("");
  const [mitraDistrict, setMitraDistrict] = useState(JHARKHAND_DISTRICTS[0] || "Ranchi");
  const [mitraBlock, setMitraBlock] = useState("");
  const [appointing, setAppointing] = useState(false);

  // WhatsApp Status Modal State
  const [waModalOpen, setWaModalOpen] = useState(false);
  const [waStatus, setWaStatus] = useState({ connected: false });
  const [checkingWa, setCheckingWa] = useState(false);

  const checkWaStatus = async () => {
    try {
      const { data } = await api.get("/admin/whatsapp/status");
      setWaStatus(data);
    } catch (e) {
      console.error(e);
      setWaStatus({ connected: false, error: e.message || "Failed to connect to gateway" });
    }
  };

  useEffect(() => {
    if (waModalOpen) {
      checkWaStatus();
      const interval = setInterval(checkWaStatus, 5000);
      return () => clearInterval(interval);
    }
  }, [waModalOpen]);

  // Sync block list with chosen district
  useEffect(() => {
    const normalized = normalizeDistrictName(mitraDistrict);
    const blocks = JHARKHAND_BLOCKS[normalized] || [];
    setMitraBlock(blocks[0] || "");
  }, [mitraDistrict]);

  // Issue Coupon Dialog State
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [issueTargetUserId, setIssueTargetUserId] = useState("");
  const [issueAmount, setIssueAmount] = useState(200);
  const [issueCustomCode, setIssueCustomCode] = useState("");
  const [issuing, setIssuing] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ovRes, mtrRes, appRes, rchRes, joinsRes, cpnRes] = await Promise.all([
        api.get("/admin/referrals/overview"),
        api.get("/admin/mitras"),
        api.get("/admin/mitras/applications"),
        api.get("/admin/recharges").catch(() => ({ data: [] })),
        api.get("/admin/referrals/joins"),
        api.get("/admin/referrals/coupons")
      ]);
      setOverview(ovRes.data);
      setMitras(mtrRes.data);
      setApplications(appRes.data);
      setRecharges(rchRes.data || []);
      setJoins(joinsRes.data);
      setCoupons(cpnRes.data);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRespondApplication = async (appId, action, name) => {
    try {
      const { data } = await api.post(`/admin/mitras/applications/${appId}/respond`, null, {
        params: { action }
      });
      toast.success(data.message || `Application ${action}d successfully.`);
      fetchData();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const handleSendCredentials = async (userId, name) => {
    try {
      const { data } = await api.post(`/admin/mitras/${userId}/send-credentials`);
      if (data.cred_msg && data.phone) {
        const cleanPhone = data.phone.replace(/\D/g, "");
        const formattedPhone = cleanPhone.startsWith("91") ? cleanPhone : `91${cleanPhone}`;
        const waUrl = `https://api.whatsapp.com/send?phone=${formattedPhone}&text=${encodeURIComponent(data.cred_msg)}`;
        window.open(waUrl, "_blank");
        toast.success(`Opening WhatsApp chat for ${name}...`);
      } else {
        toast.success(data.message || `Login credentials generated for ${name}!`);
      }
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const handleMarkRechargePaid = async (logId, phone) => {
    if (!window.confirm(`Mark ₹299 recharge as PAID for ${phone}? This will send a WhatsApp receipt to the Mitra.`)) return;
    try {
      const { data } = await api.post(`/admin/recharges/${logId}/payout`);
      toast.success(data.message || "Recharge marked as PAID!");
      fetchData();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const handleAppointMitra = async (e) => {
    e.preventDefault();
    if (!mitraName.trim() || !mitraPhone.trim() || !mitraDistrict) {
      return toast.error("Name, Phone Number and District are required.");
    }
    setAppointing(true);
    try {
      const { data } = await api.post("/admin/mitras/appoint", {
        name: mitraName.trim(),
        phone: mitraPhone.trim(),
        email: mitraEmail.trim() || null,
        district: mitraDistrict,
        block: mitraBlock
      });
      toast.success(data.message || "Vivah Mitra appointed successfully!");
      setAppointDialogOpen(false);
      setMitraName("");
      setMitraPhone("");
      setMitraEmail("");
      fetchData();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setAppointing(false);
    }
  };

  const handleRevokeMitra = async (userId, name) => {
    if (!window.confirm(`Are you sure you want to revoke Vivah Mitra status for ${name}?`)) return;
    try {
      await api.post(`/admin/mitras/revoke/${userId}`);
      toast.success(`Vivah Mitra status revoked for ${name}.`);
      fetchData();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const handleLoginAsMitra = async (userId, name) => {
    if (!window.confirm(`Login as ${name}? You will be redirected to the app as this Mitra.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${userId}/impersonate`);
      if (data.token) {
        localStorage.setItem("token", data.token);
        toast.success(`Logged in as ${name}. Redirecting...`);
        setTimeout(() => navigate("/app"), 800);
      }
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const handleDeleteMitra = async (userId, name) => {
    if (!window.confirm(`⚠️ PERMANENTLY DELETE ${name}? This will remove all their data including profile, matches, messages etc. This action CANNOT be undone!`)) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success(`Mitra ${name} has been permanently deleted.`);
      fetchData();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const handleDeleteCoupon = async (code) => {
    if (!window.confirm(`Are you sure you want to revoke/delete coupon code '${code}'?`)) return;
    try {
      await api.delete(`/admin/referrals/coupons/${code}`);
      toast.success(`Coupon '${code}' deleted/revoked successfully.`);
      fetchData();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const handleIssueCoupon = async (e) => {
    e.preventDefault();
    if (!issueTargetUserId.trim()) return toast.error("Please enter a User ID");
    setIssuing(true);
    try {
      const { data } = await api.post("/admin/referrals/issue-coupon", {
        user_id: issueTargetUserId.trim(),
        discount_amount: Number(issueAmount) || 200,
        custom_code: issueCustomCode.trim() || null
      });
      toast.success(`Coupon '${data.code}' issued successfully to ${data.user_id}!`);
      setIssueDialogOpen(false);
      setIssueTargetUserId("");
      setIssueCustomCode("");
      fetchData();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setIssuing(false);
    }
  };

  const pendingAppsCount = applications.filter(a => a.status === "pending").length;
  const pendingRechargesCount = recharges.filter(r => r.status !== "paid").length;

  // Filtered lists
  const filteredApps = applications.filter((a) => {
    const q = searchQuery.toLowerCase();
    return (
      a.name?.toLowerCase().includes(q) ||
      a.phone?.includes(q) ||
      a.district?.toLowerCase().includes(q) ||
      a.email?.toLowerCase().includes(q)
    );
  });

  const filteredRecharges = recharges.filter((r) => {
    const q = searchQuery.toLowerCase();
    return (
      r.phone?.includes(q) ||
      r.mitra?.name?.toLowerCase().includes(q) ||
      r.mitra?.district?.toLowerCase().includes(q)
    );
  });

  const filteredMitras = mitras.filter((m) => {
    const q = searchQuery.toLowerCase();
    return (
      m.name?.toLowerCase().includes(q) ||
      m.email?.toLowerCase().includes(q) ||
      m.phone?.includes(q) ||
      m.district?.toLowerCase().includes(q) ||
      m.referral_code?.toLowerCase().includes(q)
    );
  });

  const filteredJoins = joins.filter((j) => {
    const q = searchQuery.toLowerCase();
    return (
      j.name?.toLowerCase().includes(q) ||
      j.email?.toLowerCase().includes(q) ||
      j.referrer?.name?.toLowerCase().includes(q) ||
      j.referrer?.email?.toLowerCase().includes(q) ||
      j.referrer?.referral_code?.toLowerCase().includes(q)
    );
  });

  const filteredCoupons = coupons.filter((c) => {
    const q = searchQuery.toLowerCase();
    return (
      c.code?.toLowerCase().includes(q) ||
      c.owner?.name?.toLowerCase().includes(q) ||
      c.owner?.email?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Award className="w-6 h-6 text-[#6B1A24]" /> Vivah Mitra Ambassadors &amp; Referral Program
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Exclusively appoint district Vivah Mitras, process monthly ₹299 recharges, track signups &amp; issue coupons.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="gap-1 text-xs">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setWaModalOpen(true)}
            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-xs gap-1.5 rounded-lg"
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp Status
          </Button>
          <Button
            size="sm"
            onClick={() => setAppointDialogOpen(true)}
            className="bg-[#6B1A24] hover:bg-[#52131B] text-white text-xs gap-1.5 rounded-lg"
          >
            <UserCheck className="w-4 h-4" /> Appoint New Vivah Mitra
          </Button>
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm space-y-1">
          <span className="text-xs text-slate-500 font-medium uppercase tracking-wider flex items-center gap-1">
            <Inbox className="w-3.5 h-3.5 text-amber-600" /> Pending Applications
          </span>
          <div className="text-2xl font-bold text-slate-900">{pendingAppsCount}</div>
          <span className="text-[11px] text-slate-400">Ambassador requests waiting for review</span>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm space-y-1">
          <span className="text-xs text-slate-500 font-medium uppercase tracking-wider flex items-center gap-1">
            <Zap className="w-3.5 h-3.5 text-emerald-600" /> Pending ₹299 Recharges
          </span>
          <div className="text-2xl font-bold text-[#6B1A24]">{pendingRechargesCount}</div>
          <span className="text-[11px] text-slate-400">Monthly claims waiting for Admin payout</span>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm space-y-1">
          <span className="text-xs text-slate-500 font-medium uppercase tracking-wider flex items-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5 text-blue-600" /> Appointed Vivah Mitras
          </span>
          <div className="text-2xl font-bold text-slate-900">{mitras.length}</div>
          <span className="text-[11px] text-slate-400">Official District Ambassadors</span>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm space-y-1">
          <span className="text-xs text-slate-500 font-medium uppercase tracking-wider flex items-center gap-1">
            <Users className="w-3.5 h-3.5 text-purple-600" /> Referred Signups
          </span>
          <div className="text-2xl font-bold text-slate-900">{overview?.total_referred_users ?? "-"}</div>
          <span className="text-[11px] text-slate-400">Total members joined via Mitras</span>
        </div>
      </div>

      {/* Tabs & Search Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200 pb-3">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveTab("applications")}
            className={`px-4 py-2 text-xs font-semibold rounded-xl transition-all flex items-center gap-1.5 ${
              activeTab === "applications" ? "bg-[#6B1A24] text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            📥 Applications {pendingAppsCount > 0 && <span className="px-1.5 py-0.5 rounded-full bg-amber-400 text-amber-950 font-bold text-[10px]">{pendingAppsCount}</span>}
          </button>
          <button
            onClick={() => setActiveTab("recharges")}
            className={`px-4 py-2 text-xs font-semibold rounded-xl transition-all flex items-center gap-1.5 ${
              activeTab === "recharges" ? "bg-[#6B1A24] text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            ⚡ Recharges {pendingRechargesCount > 0 && <span className="px-1.5 py-0.5 rounded-full bg-emerald-500 text-white font-bold text-[10px]">{pendingRechargesCount}</span>}
          </button>
          <button
            onClick={() => setActiveTab("mitras")}
            className={`px-4 py-2 text-xs font-semibold rounded-xl transition-all ${
              activeTab === "mitras" ? "bg-[#6B1A24] text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            🛡️ Appointed Mitras ({mitras.length})
          </button>
          <button
            onClick={() => setActiveTab("joins")}
            className={`px-4 py-2 text-xs font-semibold rounded-xl transition-all ${
              activeTab === "joins" ? "bg-[#6B1A24] text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            📋 Referred Joins ({joins.length})
          </button>
          <button
            onClick={() => setActiveTab("coupons")}
            className={`px-4 py-2 text-xs font-semibold rounded-xl transition-all ${
              activeTab === "coupons" ? "bg-[#6B1A24] text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            🎟️ All Coupons ({coupons.length})
          </button>
          <button
            onClick={() => setActiveTab("leaderboard")}
            className={`px-4 py-2 text-xs font-semibold rounded-xl transition-all ${
              activeTab === "leaderboard" ? "bg-[#6B1A24] text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            🏆 Leaderboard
          </button>
        </div>

        {activeTab !== "leaderboard" && (
          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <Input
              placeholder="Search by name, phone, code..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 text-xs bg-white"
            />
          </div>
        )}
      </div>

      {/* Tab Content */}
      {loading ? (
        <div className="py-12 text-center text-sm text-slate-500">Loading data...</div>
      ) : activeTab === "applications" ? (
        /* Applications Table */
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase tracking-wider font-semibold">
                <tr>
                  <th className="px-4 py-3">Applicant Name</th>
                  <th className="px-4 py-3">District / Block</th>
                  <th className="px-4 py-3">Phone / Contact</th>
                  <th className="px-4 py-3">Message / Notes</th>
                  <th className="px-4 py-3">Applied Date</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium">
                {filteredApps.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-10 text-slate-400">
                      No Mitra applications found.
                    </td>
                  </tr>
                ) : (
                  filteredApps.map((a) => (
                    <tr key={a.app_id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="px-4 py-3 font-bold text-slate-900">{a.name}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-700">{a.district}</div>
                        {a.block && <div className="text-[11px] text-slate-400">Block: {a.block}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-mono text-slate-900">{a.phone}</div>
                        {a.email && <div className="text-[11px] text-slate-400">{a.email}</div>}
                      </td>
                      <td className="px-4 py-3 text-slate-600 max-w-xs truncate">
                        {a.notes || <span className="text-slate-400 italic">No notes</span>}
                      </td>
                      <td className="px-4 py-3 text-slate-500">
                        {a.created_at ? new Date(a.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "-"}
                      </td>
                      <td className="px-4 py-3">
                        {a.status === "pending" ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] bg-amber-100 text-amber-900 font-bold border border-amber-200">
                            ⏳ Pending Review
                          </span>
                        ) : a.status === "approved" ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] bg-emerald-100 text-emerald-800 font-semibold">
                            <CheckCircle2 className="w-3 h-3" /> Appointed
                          </span>
                        ) : a.status === "revoked" ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] bg-slate-100 text-slate-800 font-semibold border border-slate-200">
                            <XCircle className="w-3 h-3" /> Revoked
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] bg-red-100 text-red-800 font-semibold">
                            <XCircle className="w-3 h-3" /> Rejected
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right space-x-1">
                        {a.status === "pending" && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleRespondApplication(a.app_id, "approve", a.name)}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-2 py-1 h-auto"
                            >
                              <Check className="w-3.5 h-3.5 mr-1" /> Appoint
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRespondApplication(a.app_id, "reject", a.name)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs px-2 py-1 h-auto"
                            >
                              <X className="w-3.5 h-3.5 mr-1" /> Reject
                            </Button>
                          </>
                        )}
                        {a.status === "approved" && (
                          (() => {
                            const uId = a.user_id || mitras.find(m => m.phone === a.phone)?.user_id;
                            if (!uId) return null;
                            return (
                              <div className="flex items-center justify-end gap-1">
                                <button onClick={() => handleLoginAsMitra(uId, a.name)} title="Login as Mitra" className="p-1.5 rounded-md text-blue-600 hover:bg-blue-50 border border-blue-200 hover:border-blue-400 transition-colors">
                                  <LogIn className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => navigate(`/admin/users?search=${a.phone || a.email}`)} title="View Profile" className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 border border-slate-200 hover:border-slate-400 transition-colors">
                                  <Eye className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => handleSendCredentials(uId, a.name)} title="Send Login Info" className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50 border border-emerald-200 hover:border-emerald-400 transition-colors">
                                  <Send className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => handleRevokeMitra(uId, a.name)} title="Revoke Mitra" className="p-1.5 rounded-md text-orange-500 hover:bg-orange-50 border border-orange-200 hover:border-orange-400 transition-colors">
                                  <UserX className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => handleDeleteMitra(uId, a.name)} title="Delete Account" className="p-1.5 rounded-md text-red-500 hover:bg-red-50 border border-red-200 hover:border-red-400 transition-colors">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            );
                          })()
                        )}
                        {(a.status === "rejected" || a.status === "revoked") && (
                          <Button
                            size="sm"
                            onClick={() => handleRespondApplication(a.app_id, "approve", a.name)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-2 py-1 h-auto"
                          >
                            <Check className="w-3.5 h-3.5 mr-1" /> Re-Appoint
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : activeTab === "recharges" ? (
        /* Mobile Recharges Payout Table */
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase tracking-wider font-semibold">
                <tr>
                  <th className="px-4 py-3">Vivah Mitra Name</th>
                  <th className="px-4 py-3">Mobile Number</th>
                  <th className="px-4 py-3">District</th>
                  <th className="px-4 py-3">Recharge Amount</th>
                  <th className="px-4 py-3">Claim Date</th>
                  <th className="px-4 py-3">Payout Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium">
                {filteredRecharges.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-10 text-slate-400">
                      No mobile recharge claims recorded yet. (Triggered on 1st of every month for 5+ referrals)
                    </td>
                  </tr>
                ) : (
                  filteredRecharges.map((r) => (
                    <tr key={r.log_id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="px-4 py-3 font-bold text-slate-900">{r.mitra?.name || "Mitra"}</td>
                      <td className="px-4 py-3 font-mono font-bold text-slate-800">{r.phone}</td>
                      <td className="px-4 py-3 font-semibold text-slate-700">{r.mitra?.district || "Jharkhand"}</td>
                      <td className="px-4 py-3 font-bold text-emerald-700 text-sm">₹{r.amount || 299}</td>
                      <td className="px-4 py-3 text-slate-500">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "-"}
                      </td>
                      <td className="px-4 py-3">
                        {r.status === "paid" ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] bg-emerald-100 text-emerald-800 font-bold">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Paid &amp; Receipt Sent
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] bg-amber-100 text-amber-900 font-bold border border-amber-200">
                            ⏳ Pending Admin Payout
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {r.status !== "paid" && (
                          <Button
                            size="sm"
                            onClick={() => handleMarkRechargePaid(r.log_id, r.phone)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-3 py-1 h-auto font-bold"
                          >
                            <Zap className="w-3.5 h-3.5 mr-1" /> Mark as Paid
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : activeTab === "mitras" ? (
        /* Appointed Vivah Mitras Table */
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase tracking-wider font-semibold">
                <tr>
                  <th className="px-4 py-3">Vivah Mitra Name</th>
                  <th className="px-4 py-3">District / Block</th>
                  <th className="px-4 py-3">Mitra Referral Code</th>
                  <th className="px-4 py-3">Badge / Level</th>
                  <th className="px-4 py-3">Total Joins</th>
                  <th className="px-4 py-3">Appointed Date</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium">
                {filteredMitras.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-10 text-slate-400">
                      No appointed Vivah Mitras found. Click <b>"Appoint New Vivah Mitra"</b> above to appoint one!
                    </td>
                  </tr>
                ) : (
                  filteredMitras.map((m) => (
                    <tr key={m.user_id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-bold text-slate-900 flex items-center gap-1.5">
                          <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" /> {m.name}
                        </div>
                        <div className="text-[11px] text-slate-400">{m.phone} · {m.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-700">{m.district || "Jharkhand"}</div>
                        {m.block && <div className="text-[11px] text-slate-400">Block: {m.block}</div>}
                      </td>
                      <td className="px-4 py-3 font-mono font-bold text-[#6B1A24] bg-red-50/50 px-2 py-1 rounded border border-red-100 inline-block">
                        {m.referral_code || "-"}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] bg-amber-100 text-amber-900 font-semibold border border-amber-200">
                          🏅 {m.mitra_badge || m.mitra_level || "District Ambassador"}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-bold text-slate-900 text-sm">
                        {m.referral_count || 0}
                      </td>
                      <td className="px-4 py-3 text-slate-500">
                        {m.mitra_appointed_at ? new Date(m.mitra_appointed_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "-"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => handleLoginAsMitra(m.user_id, m.name)} title="Login as Mitra" className="p-1.5 rounded-md text-blue-600 hover:bg-blue-50 border border-blue-200 hover:border-blue-400 transition-colors">
                            <LogIn className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => navigate(`/admin/users?search=${m.phone || m.email}`)} title="View Profile" className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 border border-slate-200 hover:border-slate-400 transition-colors">
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => handleSendCredentials(m.user_id, m.name)} title="Send Login Info" className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50 border border-emerald-200 hover:border-emerald-400 transition-colors">
                            <Send className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => handleRevokeMitra(m.user_id, m.name)} title="Revoke Mitra" className="p-1.5 rounded-md text-orange-500 hover:bg-orange-50 border border-orange-200 hover:border-orange-400 transition-colors">
                            <UserX className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => handleDeleteMitra(m.user_id, m.name)} title="Delete Account" className="p-1.5 rounded-md text-red-500 hover:bg-red-50 border border-red-200 hover:border-red-400 transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : activeTab === "joins" ? (
        /* Referred Joins Table */
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase tracking-wider font-semibold">
                <tr>
                  <th className="px-4 py-3">Referred User (Joined)</th>
                  <th className="px-4 py-3">Inviter (Mitra)</th>
                  <th className="px-4 py-3">Referral Code Used</th>
                  <th className="px-4 py-3">Profile Status</th>
                  <th className="px-4 py-3">Joined Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium">
                {filteredJoins.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-slate-400">
                      No referred user signups found matching your search.
                    </td>
                  </tr>
                ) : (
                  filteredJoins.map((j) => (
                    <tr key={j.user_id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-bold text-slate-900">{j.name || "Anonymous User"}</div>
                        <div className="text-[11px] text-slate-400">{j.email || j.phone || j.user_id}</div>
                      </td>
                      <td className="px-4 py-3">
                        {j.referrer ? (
                          <>
                            <div className="font-semibold text-slate-800">{j.referrer.name}</div>
                            <div className="text-[11px] text-slate-400">{j.referrer.email || j.referrer.phone}</div>
                          </>
                        ) : (
                          <span className="text-slate-400 italic">Unknown</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono font-bold text-[#6B1A24]">
                        {j.referrer?.referral_code || "-"}
                      </td>
                      <td className="px-4 py-3">
                        {j.profile_complete ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] bg-emerald-100 text-emerald-800 font-semibold">
                            <CheckCircle2 className="w-3 h-3" /> Complete
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] bg-amber-100 text-amber-800 font-semibold">
                            Pending Info
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-500">
                        {j.created_at ? new Date(j.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : activeTab === "coupons" ? (
        /* Coupons Table */
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase tracking-wider font-semibold">
                <tr>
                  <th className="px-4 py-3">Coupon Code</th>
                  <th className="px-4 py-3">Discount Amount</th>
                  <th className="px-4 py-3">Owner (Mitra)</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Created / Used At</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium">
                {filteredCoupons.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-slate-400">
                      No referral coupons found matching your search.
                    </td>
                  </tr>
                ) : (
                  filteredCoupons.map((c) => (
                    <tr key={c.coupon_id || c.code} className="hover:bg-slate-50/80 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-mono font-bold text-base tracking-wider text-amber-900 bg-amber-50 px-2.5 py-1 rounded-lg border border-amber-200">
                          🎟️ {c.code}
                        </span>
                        {c.issued_by_admin && (
                          <span className="ml-2 text-[10px] bg-purple-100 text-purple-700 font-bold px-1.5 py-0.5 rounded">
                            Admin Issued
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-bold text-emerald-700 text-sm">
                        ₹{c.discount_amount} OFF
                      </td>
                      <td className="px-4 py-3">
                        {c.owner ? (
                          <>
                            <div className="font-semibold text-slate-800">{c.owner.name}</div>
                            <div className="text-[11px] text-slate-400">{c.owner.email || c.owner.phone}</div>
                          </>
                        ) : (
                          <span className="text-slate-400 font-mono text-[11px]">{c.user_id}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {c.is_used ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] bg-slate-100 text-slate-600 font-semibold">
                            <XCircle className="w-3 h-3 text-slate-400" /> Used / Redeemed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] bg-emerald-100 text-emerald-800 font-semibold">
                            <CheckCircle2 className="w-3 h-3" /> Active &amp; Redeemable
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-500">
                        <div>Created: {c.created_at ? new Date(c.created_at).toLocaleDateString() : "-"}</div>
                        {c.is_used && c.used_at && (
                          <div className="text-[11px] text-emerald-700">Used: {new Date(c.used_at).toLocaleDateString()}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCoupon(c.code)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs px-2 py-1 h-auto"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Revoke
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Referrers Leaderboard */}
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 space-y-4">
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" /> Vivah Mitra Leaderboard
            </h2>
            <div className="space-y-3">
              {(overview?.top_referrers || []).map((ref, idx) => (
                <div
                  key={ref.user_id}
                  className="p-4 rounded-xl border border-slate-100 bg-slate-50/50 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${
                      idx === 0 ? "bg-amber-400 text-amber-950" : idx === 1 ? "bg-slate-300 text-slate-800" : idx === 2 ? "bg-amber-700/20 text-amber-900" : "bg-slate-200 text-slate-600"
                    }`}>
                      #{idx + 1}
                    </div>
                    <div>
                      <div className="font-bold text-slate-900">{ref.name || "Mitra"}</div>
                      <div className="text-xs text-slate-400 font-mono">Code: {ref.referral_code}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-[#6B1A24]">{ref.referral_count} Joins</div>
                    <div className="text-[11px] text-emerald-600 font-semibold">₹{Math.floor((ref.referral_count || 0) / 5) * 200} Earned</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Block-Level Leaderboard */}
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 space-y-4">
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-emerald-500" /> Block-Level Leaderboard
            </h2>
            <div className="space-y-3">
              {(!overview?.block_leaderboard || overview.block_leaderboard.length === 0) ? (
                <div className="text-center py-10 text-xs text-slate-400">No block data available yet.</div>
              ) : (
                overview.block_leaderboard.map((block, idx) => (
                  <div
                    key={`${block.district}-${block.block}`}
                    className="p-4 rounded-xl border border-slate-100 bg-slate-50/50 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${
                        idx === 0 ? "bg-amber-400 text-amber-950" : idx === 1 ? "bg-slate-300 text-slate-800" : idx === 2 ? "bg-amber-700/20 text-amber-900" : "bg-slate-200 text-slate-600"
                      }`}>
                        #{idx + 1}
                      </div>
                      <div>
                        <div className="font-bold text-slate-900">{block.block}</div>
                        <div className="text-xs text-slate-400">District: {block.district}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-emerald-700">{block.total_joins} Joins</div>
                      <div className="text-[10px] text-muted-foreground">{block.mitras_count} Active Mitra(s)</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Dialog: Appoint New Vivah Mitra */}
      {appointDialogOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4 shadow-xl border border-slate-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-[#6B1A24]" /> Appoint New Vivah Mitra
              </h2>
              <button onClick={() => setAppointDialogOpen(false)} className="text-slate-400 hover:text-slate-600">
                ✕
              </button>
            </div>

            <form onSubmit={handleAppointMitra} className="space-y-4 text-xs">
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Mitra Full Name *</label>
                <Input
                  placeholder="e.g. Ramesh Kumar Sharma"
                  value={mitraName}
                  onChange={(e) => setMitraName(e.target.value)}
                  required
                  className="text-xs"
                  data-testid="appoint-mitra-name"
                />
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">10-Digit Mobile / WhatsApp Number *</label>
                <Input
                  type="tel"
                  placeholder="e.g. 9835760000"
                  value={mitraPhone}
                  onChange={(e) => setMitraPhone(e.target.value)}
                  required
                  className="text-xs"
                  data-testid="appoint-mitra-phone"
                />
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">District *</label>
                <select
                  value={mitraDistrict}
                  onChange={(e) => setMitraDistrict(e.target.value)}
                  className="w-full text-xs rounded-md border border-input bg-background px-3 py-2"
                  data-testid="appoint-mitra-district"
                >
                  {JHARKHAND_DISTRICTS.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Block *</label>
                <select
                  value={mitraBlock}
                  onChange={(e) => setMitraBlock(e.target.value)}
                  className="w-full text-xs rounded-md border border-input bg-background px-3 py-2"
                  data-testid="appoint-mitra-block"
                >
                  {(JHARKHAND_BLOCKS[normalizeDistrictName(mitraDistrict)] || []).map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Email Address (Optional)</label>
                <Input
                  type="email"
                  placeholder="e.g. ramesh@gmail.com"
                  value={mitraEmail}
                  onChange={(e) => setMitraEmail(e.target.value)}
                  className="text-xs"
                  data-testid="appoint-mitra-email"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setAppointDialogOpen(false)} className="text-xs">
                  Cancel
                </Button>
                <Button type="submit" disabled={appointing} className="bg-[#6B1A24] hover:bg-[#52131B] text-white text-xs gap-1.5" data-testid="submit-appoint-mitra">
                  <UserCheck className="w-4 h-4" /> {appointing ? "Appointing..." : "Appoint Vivah Mitra"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Dialog: Issue Custom Coupon */}
      {issueDialogOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4 shadow-xl border border-slate-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <Gift className="w-5 h-5 text-[#6B1A24]" /> Issue Custom Coupon
              </h2>
              <button onClick={() => setIssueDialogOpen(false)} className="text-slate-400 hover:text-slate-600">
                ✕
              </button>
            </div>

            <form onSubmit={handleIssueCoupon} className="space-y-4 text-xs">
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Target User ID</label>
                <Input
                  placeholder="e.g. usr_8f29ab42"
                  value={issueTargetUserId}
                  onChange={(e) => setIssueTargetUserId(e.target.value)}
                  required
                  className="text-xs"
                />
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Discount Amount (₹)</label>
                <Input
                  type="number"
                  placeholder="200"
                  value={issueAmount}
                  onChange={(e) => setIssueAmount(e.target.value)}
                  required
                  className="text-xs"
                />
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Custom Code (Optional)</label>
                <Input
                  placeholder="e.g. VIP200-BONUS"
                  value={issueCustomCode}
                  onChange={(e) => setIssueCustomCode(e.target.value.toUpperCase())}
                  className="font-mono text-xs uppercase"
                />
                <span className="text-[10px] text-slate-400">Leave empty to auto-generate code (REF200-XXXXXX)</span>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setIssueDialogOpen(false)} className="text-xs">
                  Cancel
                </Button>
                <Button type="submit" disabled={issuing} className="bg-[#6B1A24] hover:bg-[#52131B] text-white text-xs gap-1.5">
                  <Send className="w-3.5 h-3.5" /> {issuing ? "Issuing..." : "Issue Coupon"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Dialog: WhatsApp Status / QR Scanner */}
      {waModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4 shadow-xl border border-slate-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-[#6B1A24]" /> WhatsApp API Configuration
              </h2>
              <button onClick={() => setWaModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between bg-slate-50 p-4 rounded-xl border border-slate-100">
                <div>
                  <div className="font-semibold text-slate-800 text-sm">Connection Status</div>
                  <div className="text-xs text-slate-400">Baileys API Service</div>
                </div>
                <div>
                  {waStatus.connected ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                      ● Active & Connected
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 border border-red-200">
                      ● Disconnected
                    </span>
                  )}
                </div>
              </div>

              {waStatus.connected ? (
                <div className="text-center py-6 space-y-2">
                  <div className="text-4xl">✅</div>
                  <h3 className="font-bold text-slate-800 text-sm">WhatsApp Connected successfully!</h3>
                  <p className="text-xs text-slate-500 max-w-sm mx-auto">
                    Automated welcome messages, milestone alerts, and recharge notifications will be sent directly through the connected account.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="text-xs text-slate-500 bg-amber-50 border border-amber-100 p-3 rounded-lg leading-relaxed">
                    <strong>⚠️ Action Required:</strong> To send messages automatically, please scan the QR code below using your phone's WhatsApp:
                    <ol className="list-decimal pl-4 mt-1.5 space-y-1">
                      <li>Open WhatsApp on your phone</li>
                      <li>Go to <strong>Linked Devices</strong> (or settings menu)</li>
                      <li>Tap on <strong>Link a Device</strong> and point your camera here</li>
                    </ol>
                  </div>

                  <div className="flex items-center justify-center bg-slate-50 border border-slate-200 rounded-xl p-4 min-h-[340px]">
                    <iframe
                      src={`${window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1" ? "http://127.0.0.1:8000" : ""}/api/admin/whatsapp/qr?token=${encodeURIComponent(localStorage.getItem("jjodi_token"))}`}
                      className="w-full h-[320px] border-0 rounded-lg bg-white"
                      title="WhatsApp QR"
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={checkWaStatus} className="text-xs">
                  Refresh Status
                </Button>
                <Button size="sm" onClick={() => setWaModalOpen(false)} className="bg-[#6B1A24] hover:bg-[#52131B] text-white text-xs">
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
